import { useContext } from 'react';
import { UseFormReturn } from 'react-hook-form';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore lodash is in the parent dir
import _ from 'lodash';

import { SidebarStateContext } from 'contexts/sidebarStateContext';
import { useServiceFeatures } from 'hooks/useServiceContext';
import { toast } from 'hooks/useToaster';
import { submitQuoteViaURL } from 'lib/api/submitQuoteViaURL';
import {
  QuoteConfirmationStatus,
  QuoteTerminatingAction,
  updateQuoteRequestSuggestion,
} from 'lib/api/updateQuoteRequestSuggestion';
import { Email } from 'types/Email';
import { Maybe } from 'types/UtilityTypes';
import { SuggestionPipelines } from 'types/suggestions/CoreSuggestions';
import { SuggestionStatus } from 'types/suggestions/LoadSuggestions';
import { applyCompleteKitSuggestions } from 'utils/completeKitSuggestion';
import { calculatePricing } from 'utils/priceCalculations';

import { useQuickQuoteContext } from '../context/QuickQuoteContext';
import { QuickQuoteInputs } from '../types';
import { processQuoteTMSSubmission } from '../utils/submission';

export const useQuoteSubmissionViaURL = (
  formMethods: UseFormReturn<QuickQuoteInputs, any, QuickQuoteInputs>,
  email: Maybe<Email>
) => {
  const {
    currentState: { clickedSuggestion },
    setCurrentState,
  } = useContext(SidebarStateContext);

  const {
    serviceFeaturesEnabled: { isTMSQuoteSubmissionEnabled },
  } = useServiceFeatures();

  const context = useQuickQuoteContext();
  const {
    isSubmitToTMS,
    setCreatedQuoteId,
    setCreatedQuoteInternalId,
    parentQuoteRequestId,
    carrierCost,
    carrierCostType,
    profit,
    profitType,
    finalPrice,
    maxDistance,
    setLoadingDraftReply,
  } = context;

  const { getValues, setError } = formMethods;

  // Handles quote submission via URL
  const submitQuoteViaURLHandler = async (
    selectedQuickQuoteId?: number
  ): Promise<void> => {
    if (!email) return;

    if (!getValues('quoteNumber')) {
      setError(
        'quoteNumber',
        {
          message: 'Quote number is required',
        },
        { shouldFocus: true }
      );
      return;
    }

    if (!finalPrice || finalPrice <= 0) {
      return;
    }

    setLoadingDraftReply(true);

    const roundedFinalPrice = _.round(finalPrice);
    const expirationDateValue = getValues('quoteExpirationDate');
    const etaValue = getValues('quoteEta');

    const res = await submitQuoteViaURL(email.id, {
      quoteAmount: roundedFinalPrice,
      quoteNumber: getValues('quoteNumber') || '',
      expirationDate: expirationDateValue
        ? new Date(expirationDateValue)
        : new Date(),
      eta: etaValue ? new Date(etaValue) : new Date(),
    });

    if (res.isOk()) {
      toast({
        description: 'Successfully submitted quote via hyperlink.',
        variant: 'success',
      });
    } else {
      toast({
        description: res.error.message,
        variant: 'destructive',
      });
      setLoadingDraftReply(false);
      return; // don't proceed with TMS submission if quote submission via URL fails
    }

    // Handle TMS quote submission if enabled
    if (isTMSQuoteSubmissionEnabled && isSubmitToTMS) {
      const customerId = getValues('customerName');
      if (!customerId) {
        setLoadingDraftReply(false);
        return;
      }

      const createdQuote = await processQuoteTMSSubmission({
        customerId: customerId.toString(),
        finalPrice: finalPrice,
        getValues,
        setError,
      });

      const createdQuoteId = createdQuote?.quoteExternalId ?? '';
      const createdQuoteInternalId =
        createdQuote?.quoteId != null && createdQuote.quoteId > 0
          ? createdQuote.quoteId
          : null;
      setCreatedQuoteId(createdQuoteId);
      setCreatedQuoteInternalId(createdQuoteInternalId);
      setLoadingDraftReply(false);
    }

    // Calculate final carrier cost and margin
    const { flatCarrierCost: finalFlatCarrierCost, finalProfit } =
      calculatePricing(
        carrierCost,
        carrierCostType,
        profit,
        profitType,
        maxDistance
      );

    await updateQuoteRequestSuggestion(
      parentQuoteRequestId,
      SuggestionStatus.Accepted,
      {
        finalQuotePrice: _.round(finalPrice ?? 0),
        finalMargin: _.round(finalProfit),
        marginType: profitType,
        finalCarrierCost: _.round(finalFlatCarrierCost),
        carrierCostType: carrierCostType,
        customerExternalTMSId: getValues('customerName'),
        quoteConfirmationStatus: QuoteConfirmationStatus.UserConfirmed,
        quoteTerminatingAction: QuoteTerminatingAction.Submit,
      },
      selectedQuickQuoteId
    );

    // Mark suggestion as Completed! and advance within the QQ pipeline
    if (clickedSuggestion) {
      setCurrentState((prevState) => ({
        ...prevState,
        ...applyCompleteKitSuggestions(
          prevState,
          clickedSuggestion.id,
          SuggestionPipelines.QuickQuote
        ),
      }));
    }

    setLoadingDraftReply(false);
  };

  return {
    submitQuoteViaURL: submitQuoteViaURLHandler,
  };
};
